<template>
    <glm-layout>
        <world-map :specials="$page.props.mapCountries" height="200px">
            <div class="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 flex items-center h-full justify-start">
                <div class="rounded-md max-w-lg inline-block p-2 bg-grey-700 text-white mt-3">
                    <h3 class="font-bold font-serif text-3xl">
                        JWD
                    </h3>
                </div>
            </div>
        </world-map>

        <dialog-modal :show="snapshotImage !== null" @close="snapshotImage = null">
            <template #title>
                Download Graph as Image
            </template>

            <template #content>
                <div class="flex justify-center items-center">
                    <img :src="snapshotImage" alt="Snapshot" class="w-full border rounded" />
                </div>

                <div class="mt-3 mb-4 text-sm shadow-sm rounded flex items-stretch justify-start">
                    <div class="p-2 bg-glm-blue text-blue-100 flex items-center justify-center rounded-l">
                        <font-awesome-icon
                            :icon="['fad', 'info']"
                            class="fa-fw"
                            size="lg"
                        ></font-awesome-icon>
                    </div>

                    <p class="p-2 bg-blue-100 text-glm-blue flex-1 rounded-r">
                        You can download the graph as an image by right-clicking on the image and selecting "Save image
                        as..." or by using the button below.
                    </p>
                </div>

                <a
                    :href="snapshotImage"
                    download="jwp-scatter.png"
                    class="block font-condensed focus:ring-offset-2 focus:outline-none outline-none focus:ring focus:ring-glm-red focus:ring-opacity-20 transition-all shadow-sm bg-glm-red hover:bg-red-800 text-white font-bold py-2 px-4 rounded-md block w-full md:inline-block md:w-auto mx-auto"
                >
                    Download

                    <font-awesome-icon :icon="['fad', 'download']" class="ml-3"></font-awesome-icon>
                </a>

                <p class="mt-4 text-sm text-gray-500 hidden">
                    If you are unable to download the image, use this
                    <a @click.prevent="downloadChartOld"
                        target="_blank"
                        class="cursor-pointer text-glm-blue hover:text-blue-600 no-underline"
                        role="button"
                    >link</a>.<br><br>
                    This will open the image in a new tab, which you can then download by right-clicking and selecting
                    "Save image as...". <span class="text-red-500 italic">Quality may be reduced</span> if you use this approach.
                </p>
            </template>

            <template #footer>
                <secondary-button @click.native="snapshotImage = null">
                    Close
                </secondary-button>
            </template>
        </dialog-modal>

        <dialog-modal :show="countryYearData !== null" @close="countryYearData = null">
            <template #title>
                List of [country, year] included
            </template>

            <template #content>
                <table
                    class="w-full whitespace-nowrap rounded bg-white divide-y divide-gray-300 overflow-hidden shadow"
                >
                    <thead class="bg-gray-900">
                    <tr class="text-white">
                        <th class="font-semibold text-sm uppercase px-6 py-4 text-left">Country</th>
                        <th class="font-semibold text-sm uppercase px-6 py-4 text-right">Year</th>
                    </tr>
                    </thead>

                    <tbody class="divide-y divide-gray-200">
                    <tr v-for="(item, idx) in countryYearData" :key="idx" :class="{'bg-gray-200' : idx % 2 !== 0}">
                        <td class="px-6 py-4">
                            {{ item.country }}
                        </td>

                        <td class="px-6 py-4 text-right">
                            {{ item.year }}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </template>

            <template #footer>
                <secondary-button @click.native="countryYearData = null">
                    Close
                </secondary-button>
            </template>
        </dialog-modal>

        <div class="section py-12">
            <page-content>
                <div class="flex justify-center items-center px-2 py-3">
                    <inertia-link :href="route('charts')" class="px-2 py-1 bg-gray-100 rounded-md">
                        heatmap
                    </inertia-link>

                    <inertia-link
                        :href="route('scatter')"
                        class="px-2 py-1 bg-glm-red text-white font-semibold rounded-md ml-4"
                    >
                        scatter
                    </inertia-link>
                </div>

                <h2 class="font-semibold my-3 text-glm-blue text-3xl font-serif">
                    Select Data
                </h2>

                <div
                    v-if="form.sample.length === 0 || form.year.length === 0"
                    class="text-sm shadow-sm rounded flex items-stretch justify-start my-4"
                >
                    <div class="px-3 py-2 bg-yellow-600 text-yellow-100 flex items-center justify-center rounded-l">
                        <font-awesome-icon
                            :icon="['fad', 'info']"
                            class="fa-fw"
                            size="lg"
                        ></font-awesome-icon>
                    </div>

                    <div class="px-3 py-2 bg-yellow-100 text-yellow-600 flex-1 rounded-r">
                        <div class="font-medium">Please select at least one sample and one year.</div>
                    </div>
                </div>

                <div v-else-if="noData" class="text-sm shadow-sm rounded flex items-stretch justify-start my-4">
                    <div class="px-3 py-2 bg-glm-red text-red-100 flex items-center justify-center rounded-l">
                        <font-awesome-icon
                            :icon="['fad', 'siren-on']"
                            class="fa-fw"
                            size="lg"
                        ></font-awesome-icon>
                    </div>

                    <div class="px-3 py-2 bg-red-100 text-glm-red flex-1 rounded-r">
                        <div class="font-medium">No data available for this selection.</div>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-x-2">
                    <div class="mb-2">
                        <label class="block text-sm font-semibold text-glm-black mb-1">
                            Sample
                        </label>

                        <Multiselect
                            ref="multiselectS"
                            v-model="form.sample"
                            :options="sampleOptions"
                            mode="tags"
                            noResultsText="All samples have already been selected."
                            placeholder="Please select..."
                            @select="$refs.multiselectS.close()"
                        >
                            <template v-slot:tag="{ option, handleTagRemove, disabled }">
                                <div class="multiselect-tag bg-glm-red">
                                    {{ option.label }}

                                    <i
                                        v-if="!disabled"
                                        @click.prevent
                                        @mousedown.prevent.stop="handleTagRemove(option, $event)"
                                    />
                                </div>
                            </template>
                        </Multiselect>
                    </div>

                    <div class="mb-2">
                        <label class="block text-sm font-semibold text-glm-black mb-1">
                            Year
                        </label>

                        <Multiselect
                            ref="multiselectY"
                            v-model="form.year"
                            :maxHeight="500"
                            :options="yearOptions"
                            mode="tags"
                            noOptionsText="Please select at least one sample first."
                            noResultsText="All years have already been selected."
                            placeholder="Please select..."
                            @select="$refs.multiselectY.close()"
                        >
                            <template v-slot:tag="{ option, handleTagRemove, disabled }">
                                <div class="multiselect-tag bg-glm-red">
                                    {{ option.label }}

                                    <i
                                        v-if="!disabled"
                                        @click.prevent
                                        @mousedown.prevent.stop="handleTagRemove(option, $event)"
                                    />
                                </div>
                            </template>
                        </Multiselect>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-x-2">
                    <app-input
                        id="split"
                        v-model="form.split"
                        :options="splitOptions"
                        type="select"
                    >
                        <template #label>
                            Split
                        </template>
                    </app-input>

                    <app-input
                        id="var"
                        v-model="selectedVar"
                        :options="varList"
                        type="group"
                    >
                        <template #label>
                            Variable
                        </template>
                    </app-input>
                </div>
            </page-content>
        </div>

        <div class="section pt-12 pb-24 bg-grey-100" style="min-height: 800px">
            <page-content>
                <div class="grid grid-cols-1 lg:grid-cols-2">
                    <template v-for="(chart, k) in charts">
                        <div class="relative">
                            <div class="absolute top-20 right-12 z-10">
                                <a
                                    :download="'scatter_' + chart.chartOptions.title.replace(/\s/g, '_') + '.png'"
                                    class="cursor-pointer text-glm-blue hover:text-blue-600 no-underline"
                                    role="button"
                                    @click.prevent="downloadChart(k)"
                                >
                                    <font-awesome-icon
                                        :icon="['fad', 'download']"
                                        class="fa-fw"
                                    ></font-awesome-icon>
                                </a>

                                <a
                                    class="cursor-pointer text-glm-blue hover:text-blue-600 no-underline"
                                    role="button"
                                    @click="showYears(k)"
                                >
                                    <font-awesome-icon
                                        :icon="['fad', 'clipboard-list']"
                                        class="fa-fw"
                                    ></font-awesome-icon>
                                </a>
                            </div>

                            <GChart
                                v-if="chart.chartData.length > 0"
                                :key="k"
                                :data="chart.chartData"
                                :options="chart.chartOptions"
                                :settings="{ packages: ['corechart'] }"
                                type="ScatterChart"
                                @ready="(c, g) => onReadyEvent(c, g, k)"
                            />

                            <h6
                                class="mb-4 text-sm"
                                v-html="chart.chartOptions._description"
                            ></h6>
                        </div>
                    </template>
                </div>
            </page-content>
        </div>

        <div class="section pt-12 pb-24 bg-white" v-if="selectedVarObject">
            <page-content>
                <h2 class="font-semibold mb-3 text-glm-blue text-3xl font-serif">
                    Variable Information
                </h2>

                <div class="grid gap-3 grid-cols-1 md:grid-cols-2">
                    <div class="col-span-1">
                        <div class="grid gap-2 grid-cols-1 md:grid-cols-6">
                            <div class="col-span-1">
                                <span class="text-gray-500">Value</span>
                            </div>

                            <div class="col-span-5">
                                {{ selectedVarObject.value }}
                            </div>
                        </div>

                        <div class="grid gap-2 grid-cols-1 md:grid-cols-6">
                            <div class="col-span-1">
                                <span class="text-gray-500">Label</span>
                            </div>

                            <div class="col-span-5">
                                {{ selectedVarObject.label }}
                            </div>
                        </div>
                    </div>

                    <div class="col-span-1" v-if="selectedVarObject.definition">
                        <span class="text-gray-500">Definition</span><br>
                        {{ selectedVarObject.definition }}
                    </div>
                </div>

                <div class="grid gap-3 grid-cols-1 lg:grid-cols-2 mt-3">
                    <div class="col-span-1">
                        <span class="text-gray-500">DHS implementation</span>

                        <p v-if="selectedVarObject.dhs">
                            {{ selectedVarObject.dhs }}
                        </p>

                        <p v-else>
                            <span class="text-gray-400">
                                Not defined
                            </span>
                        </p>
                    </div>

                    <div class="col-span-1">
                        <span class="text-gray-500">IPUMS implementation</span>

                        <p v-if="selectedVarObject.ipums">
                            {{ selectedVarObject.ipums }}
                        </p>

                        <p v-else>
                            <span class="text-gray-400">
                                Not defined
                            </span>
                        </p>
                    </div>
                </div>
            </page-content>
        </div>
    </glm-layout>
</template>

<script>
import GlmLayout from '@/Layouts/GlmLayout'
import WorldMap from '@/Components/WorldMap'
import { GChart } from 'vue-google-charts'
import PageContent from '@/Components/PageContent'
import AppInput from '@/Components/AppInput'
import Multiselect from '@vueform/multiselect/dist/multiselect.vue2.js'
import DialogModal from '../Jetstream/DialogModal'
import SecondaryButton from '../Jetstream/SecondaryButton'

export default {
    components: {
        SecondaryButton,
        DialogModal,
        AppInput,
        PageContent,
        GlmLayout,
        WorldMap,
        GChart,
        Multiselect,
    },

    props: ['sampleOptions', 'splitOptions', 'subOptions', 'varOptions'],

    data () {
        return {
            form: {
                sample: ['women', 'men', 'pooled'],
                // sample: ['pooled'],
                split: 'cross_country',
                year: ['1990-2020'],
            },

            oForm: {
                sample: [],
                split: 'cross_country',
                year: [],
            },

            value: [],

            selectedSub: null,
            selectedGender: null,
            selectedVar: 'inwork',

            yearOptions: [],

            noData: false,

            countryYearData: null,
            selectedVarObject: null,

            snapshotImage: null,
            snapshotKey: null,

            // sampleOptions: [
            //     {label: 'Women', value: 'women'},
            //     {label: 'Men', value: 'men'},
            //     {label: 'Pooled', value: 'pooled'},
            // ],

            // splitOptions: [
            //     {label: 'None', value: 'cross_country'},
            //     {label: 'Age, 5 years brackets', value: 'age5year'},
            //     {label: 'Fertility: Respondent has at least one children', value: 'children_d'},
            //     {label: 'Decade of birth', value: 'decob'},
            //     {label: 'Highest lvl of education', value: 'educlvl'},
            //     {label: 'Marital status', value: 'marital'},
            //     {label: 'Wealth score above median', value: 'wealth_hi'},
            //     {label: 'Wealth score, quintile', value: 'wealth_pca_q'},
            //     {label: 'Urban residents', value: 'urban'},
            //     {label: 'Income class', value: 'income_class'},
            // ],

            // subOptions: {
            //     'age5year': [
            //         {label: '15-19', value: 1},
            //         {label: '20-24', value: 2},
            //         {label: '25-29', value: 3},
            //         {label: '30-34', value: 4},
            //         {label: '35-39', value: 5},
            //         {label: '40-45', value: 6},
            //         {label: '45-49', value: 7},
            //     ],
            //
            //     'children_d': [
            //         {label: 'No', value: 1},
            //         {label: 'Yes', value: 2},
            //     ],
            //
            //     'decob': [
            //         {label: 'Born after 1990', value: 1},
            //         {label: 'Born 1980 - 1989', value: 2},
            //         {label: 'Born 1970 - 1979', value: 3},
            //         {label: 'Born 1960 - 1969', value: 4},
            //         {label: 'Born 1950 - 1959', value: 5},
            //         {label: 'Born before 1950', value: 6},
            //     ],
            //
            //     'educlvl': [
            //         {label: 'None', value: 1},
            //         {label: 'Primary', value: 2},
            //         {label: 'Secondary', value: 3},
            //         {label: 'Higher', value: 4},
            //     ],
            //
            //     'income_class': [
            //         {label: 'Low Income', value: 1},
            //         {label: 'Lower Middle Income', value: 2},
            //         {label: 'Upper Middle Income', value: 3},
            //         {label: 'High Income', value: 4},
            //     ],
            //
            //     'marital': [
            //         {label: 'Single/Never married', value: 1},
            //         {label: 'Married/in union', value: 2},
            //         {label: 'Separated/divorced/widowed', value: 3},
            //     ],
            //
            //     'urban': [
            //         {label: 'Rural', value: 1},
            //         {label: 'Urban', value: 2},
            //     ],
            //
            //     'wealth_hi': [
            //         {label: 'Below median', value: 1},
            //         {label: 'Above median', value: 2},
            //     ],
            //
            //     'wealth_pca_q': [
            //         {label: 'Poorest', value: 1},
            //         {label: 'Poorer', value: 2},
            //         {label: 'Middle', value: 3},
            //         {label: 'Richer', value: 4},
            //         {label: 'Richest', value: 5},
            //     ],
            // },

            // varOptions: [
            //     {
            //         label: 'Employment',
            //         options: [
            //             {value: 'inwork', label: 'Employment rate'},
            //             {value: 'employee', label: 'Employees, as a share of workforce'},
            //             {value: 'employee_all', label: 'Employees, as a share of population'},
            //             {value: 'selfemployed', label: 'Self-employment, as a share of workforce'},
            //             {value: 'selfemployed_all', label: 'Self-employment, as a share of population'},
            //             {value: 'paidwk', label: 'Paid work, as a share of workforce'},
            //             {value: 'paidwk_all', label: 'Paid work, as a share of population'},
            //             {value: 'unpaidwk', label: 'Unpaid work, as a share of workforce'},
            //             {value: 'unpaidwk_all', label: 'Unpaid work, as a share of population'},
            //             {value: 'allyearwk', label: 'Permanent employment, as a share of workforce'},
            //             {value: 'allyearwk_all', label: 'Permanent employment, as a share of population'},
            //             {value: 'seasonalwk', label: 'Seasonal employment, as a share of workforce'},
            //             {value: 'seasonalwk_all', label: 'Seasonal employment, as a share of population'},
            //             {value: 'sector_agri', label: 'Employment in agriculture, as a share of workforce'},
            //             {value: 'sector_agri_all', label: 'Employment in agriculture, as a share of population'},
            //             {
            //                 value: 'sector_nonagri',
            //                 label: 'Employment outside of agriculture, as a share of workforce',
            //             },
            //             {
            //                 value: 'sector_nonagri_all',
            //                 label: 'Employment outside of agriculture, as a share of population',
            //             },
            //             {value: 'sector_manuf', label: 'Employment in manufacturing, as a share of workforce'},
            //             {value: 'sector_manuf_all', label: 'Employment in manufacturing, as a share of population'},
            //             {value: 'sector_serv', label: 'Employment in service sector, as a share of workforce'},
            //             {value: 'sector_serv_all', label: 'Employment in service sector, as a share of population'},
            //             {value: 'public', label: 'Employment in the public sector, as a share of workforce'},
            //             {value: 'public_all', label: 'Employment in the public sector, as a share of population'},
            //             {value: 'private', label: 'Employment in the private sector, as a share of workforce'},
            //             {value: 'private_all', label: 'Employment in the private sector, as a share of population'},
            //             {value: 'highskillwk', label: 'High skill workers, as a share of workforce'},
            //             {value: 'highskillwk_all', label: 'High skill workers, as a share of population'},
            //             {value: 'whitecollar', label: 'White collar workers, as a share of workforce'},
            //             {value: 'whitecollar_all', label: 'White collar workers, as a share of population'},
            //             {value: 'bluecollar', label: 'Blue collar workers, as a share of workforce'},
            //             {value: 'bluecollar_all', label: 'Blue collar workers, as a share of population'},
            //             {value: 'self_paid', label: 'Self-employed and paid, as a share of workforce'},
            //             {value: 'self_unpaid', label: 'Self-employed and unpaid, as a share of workforce'},
            //             {value: 'employee_paid', label: 'Employees and paid, as a share of workforce'},
            //             {value: 'employee_unpaid', label: 'Employess and unpaid, as a share of workforce'},
            //             {value: 'self_agri', label: 'Self-employed in agriculture, as a share of workforce'},
            //             {
            //                 value: 'self_nonagri',
            //                 label: 'Self-employed outside of agriculture, as a share of workforce',
            //             },
            //             {value: 'self_manuf', label: 'Self-employed in manufacturing, as a share of workforce'},
            //             {value: 'self_serv', label: 'Self-employed in service sector, as a share of workforce'},
            //             {value: 'employee_agri', label: 'Employees in agriculture, as a share of workforce'},
            //             {
            //                 value: 'employee_nonagri',
            //                 label: 'Employees outside of agriculture, as a share of workforce',
            //             },
            //             {value: 'employee_manuf', label: 'Employees in manufacturing, as a share of workforce'},
            //             {value: 'employee_serv', label: 'Employees in service sector, as a share of workforce'},
            //         ],
            //     },
            //
            //     {
            //         label: 'Education',
            //         options: [
            //             {special: true, value: 'edyrtotal', label: 'Years of education, average'},
            //             {value: 'educlvl_1', label: 'Highest level of education: None, share of population'},
            //             {value: 'educlvl_2', label: 'Highest level of education: Primary, share of population'},
            //             {value: 'educlvl_3', label: 'Highest level of education: Secondary, share of population'},
            //             {value: 'educlvl_4', label: 'Highest level of education: Tertiary, share of population'},
            //             {value: 'no_educ', label: 'No formal education, as a share of population'},
            //             {value: 'atleastprimary', label: 'Completed primary education, as a share of population'},
            //             {value: 'atleastsecondary', label: 'Completed secondary education, as a share of population'},
            //         ],
            //     },
            //
            //     {
            //         label: 'Marriage',
            //         options: [
            //             {value: 'marital_1', label: 'Marital Status: Single/Never married, share of population'},
            //             {value: 'marital_2', label: 'Marital Status: Married/in union, share of population'},
            //             {
            //                 value: 'marital_3',
            //                 label: 'Marital Status: Separated/divorced/widowed, share of population',
            //             },
            //             {special: true, value: 'agefrstmar', label: 'Age at marriage, average'},
            //         ],
            //     },
            //
            //     {
            //         label: 'Fertility',
            //         options: [
            //             {value: 'children_d', label: 'Has at least one child, as a share of the population'},
            //             {special: true, value: 'ageat1stbirth', label: 'Age at first birth, average'},
            //         ],
            //     },
            //
            //     {
            //         label: 'Migration',
            //         options: [
            //             {value: 'migrant', label: 'Migrants, as a share of population'},
            //             {value: 'migchild', label: 'Migrated as child [0,12], as a share of population'},
            //             {value: 'migteen', label: 'Migrated as teenager [13,18], as a share of population'},
            //             {value: 'migadult', label: 'Migrated as adult [19+], as a share of population'},
            //             {
            //                 special: true,
            //                 value: 'agedif_marmig',
            //                 label: 'Difference between marriage and last migration spell,  average years',
            //             },
            //             {
            //                 value: 'migatmar',
            //                 label: 'Migrated within 1 year from marrying, share of population that has migrated and married',
            //             },
            //             {
            //                 value: 'migatmar_all',
            //                 label: 'Migrated within 1 year from marrying, share of population that has migrated and married once',
            //             },
            //             {
            //                 value: 'migpremar',
            //                 label: 'Migrated within 1 year before marrying, share of population that has migrated and married',
            //             },
            //             {
            //                 value: 'migpremar_all',
            //                 label: 'Migrated within 1 year before marrying, share of population that has migrated and married once',
            //             },
            //             {
            //                 value: 'migpostmar',
            //                 label: 'Migrated within 1 year after marrying, share of population that has migrated and married',
            //             },
            //             {
            //                 value: 'migpostmar_all',
            //                 label: 'Migrated within 1 year after marrying, share of population that has migrated and married once',
            //             },
            //             {
            //                 value: 'kid_ruraltourban',
            //                 label: 'Migrated from rural to urban area, as a share of migrants',
            //             },
            //             {
            //                 value: 'kid_urbantorural',
            //                 label: 'Migrated from urban to rural area, as a share of migrants',
            //             },
            //             {
            //                 value: 'kid_ruraltorural',
            //                 label: 'Migrated from rural to rural area, as a share of migrants',
            //             },
            //             {
            //                 value: 'kid_urbantourban',
            //                 label: 'Migrated from urban to urban area, as a share of migrants',
            //             },
            //         ],
            //     },
            //
            //     {
            //         label: 'Characteristics',
            //         options: [
            //             {value: 'decob_1', label: 'Born after 1990, as a share of population'},
            //             {value: 'decob_2', label: 'Born 1980 - 1989, as a share of population'},
            //             {value: 'decob_3', label: 'Born 1970 - 1979, as a share of population'},
            //             {value: 'decob_4', label: 'Born 1960 - 1969, as a share of population'},
            //             {value: 'decob_5', label: 'Born 1950 - 1959, as a share of population'},
            //             {value: 'decob_6', label: 'Born before 1950, as a share of population'},
            //             {value: 'age5year_1', label: 'Age 15-19, share of population'},
            //             {value: 'age5year_2', label: 'Age 20-24, share of population'},
            //             {value: 'age5year_3', label: 'Age 25-29, share of population'},
            //             {value: 'age5year_4', label: 'Age 30-34, share of population'},
            //             {value: 'age5year_5', label: 'Age 35-39, share of population'},
            //             {value: 'age5year_6', label: 'Age 40-45, share of population'},
            //             {value: 'age5year_7', label: 'Age 45-49, share of population'},
            //             {special: true, value: 'age', label: 'Age, median'},
            //             {value: 'wealth_pca_q_1', label: 'Wealth quintile #1 (poorest), as a share of population'},
            //             {value: 'wealth_pca_q_2', label: 'Wealth quintile #2, as a share of population'},
            //             {value: 'wealth_pca_q_3', label: 'Wealth quintile #3, as a share of population'},
            //             {value: 'wealth_pca_q_4', label: 'Wealth quintile #4, as a share of population'},
            //             {value: 'wealth_pca_q_5', label: 'Wealth quintile #5 (richest), as a share of population'},
            //             {value: 'urban', label: 'Urban Residents, share of population'},
            //         ],
            //     },
            // ],

            charts: [],

            varToLabel: {},

            defaultChartOptions: {
                title: '',
                hAxis: {
                    title: 'GDP p/c, log',
                    minValue: 0,
                    maxValue: 11,
                    viewWindow: {
                        min: 6,
                        max: 11,
                    },
                    ticks: [6, 7, 8, 9, 10, 11],
                },
                vAxis: {
                    title: '',
                    minValue: 0,
                    maxValue: 1,
                    viewWindow: {
                        min: 0,
                        max: 1,
                    },
                    ticks: [0, .2, .4, .6, .8, 1],
                },
                chartArea: {
                    width: '85%',
                    // height: '95%',
                },
                height: 600,
                backgroundColor: '#f5f5f5',
                defaultColor: '#000',
                datalessRegionColor: '#fff',
                legend: {
                    position: 'bottom',
                },
                crosshair: { trigger: 'both', orientation: 'both' },
                trendlines: {},
                series: {},
            },
        }
    },

    mounted () {
        this.getYearData(this.form)

        this.getData(this.form)
    },

    computed: {
        varList () {
            if (!this.form.split) {
                return this.varOptions
            }

            if (this.form.split === 'cross_country') {
                return this.varOptions
            }

            let ret = [], _this = this

            this.varOptions.forEach(function (option) {
                ret.push({
                    label: option.label,
                    options: option.options.filter(e => !e.value.startsWith(_this.form.split)),
                })
            })

            return ret
        },
    },

    watch: {
        form: {
            handler (n) {
                this.charts = []

                if (n.sample !== this.oForm.sample || n.split !== this.oForm.split) {
                    // n.year = []
                    this.getYearData(n)
                }

                if (n.sample.length !== 0 && n.split && n.year.length !== 0) {
                    this.getData(n)
                }

                this.oForm = { ...n }
            },

            deep: true,
        },

        selectedSub () {
            this.getData(this.form)
        },

        selectedGender () {
            this.getData(this.form)
        },

        selectedVar () {
            this.getData(this.form)
        },
    },

    methods: {
        // https://stackoverflow.com/a/52092093/3852382
        openBase64InNewTab (data, mimeType) {
            let byteCharacters = window.atob(data)
            let byteNumbers = new Array(byteCharacters.length)

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
            }

            let byteArray = new Uint8Array(byteNumbers)
            let file = new Blob([byteArray], { type: mimeType + ';base64' })
            let fileURL = URL.createObjectURL(file)
            window.open(fileURL)
        },

        downloadChart (key) {
            // const s = this.charts[key]['chartOptions']['imageUrl'].replace('data:image/png;base64,', '')
            // this.openBase64InNewTab(s, 'image/png')

            this.snapshotImage = this.charts[key]['chartOptions']['imageUrl']
            this.snapshotKey = key

            // DOES NOT WORK ON SAFARI!
            // window.open(this.charts[key]['chartOptions']['imageUrl'], '_blank').focus()
        },

        downloadChartOld () {
            if (this.snapshotImage === null) {
                return
            }

            const s = this.charts[this.snapshotKey]['chartOptions']['imageUrl'].replace('data:image/png;base64,', '')
            this.openBase64InNewTab(s, 'image/png')

            // this.snapshotImage = this.charts[key]['chartOptions']['imageUrl']

            // DOES NOT WORK ON SAFARI!
            // window.open(this.charts[key]['chartOptions']['imageUrl'], '_blank').focus()
        },

        downloadYears (data) {
            window.open('data:text/json,' + encodeURIComponent(data), '_blank').focus()
        },

        showYears (key) {
            let originalData = this.charts[key]['originalData'],
                list = {}

            originalData.forEach(item => {
                if (!list.hasOwnProperty(item['country_id'])) {
                    list[item['country_id']] = {
                        country: item['country_label'],
                        year: item['year'],
                    }
                } else {
                    list[item['country_id']]['year'] = item['year']
                }
            })

            this.countryYearData = Object.values(list)
        },

        onReadyEvent (chart, google, key) {
            let _this = this

            google.visualization.events.addListener(chart, 'ready', function () {
                _this.$set(_this.charts[key]['chartOptions'], 'imageUrl', chart.getImageURI())
            })
        },

        findGroupWithVariable (variable) {
            return this.varOptions.filter(v => {
                return v.options.filter(v_ => {
                    return v_.value === variable
                })[0]
            })[0]
        },

        findVariableInGroup (variable, group) {
            return group.options.filter(v => {
                return v.value === variable
            })[0]
        },

        getYearData (form) {
            this.yearOptions = [
                { label: 'All', value: '1990-2020' },
                { label: '1990 - 2000', value: '1990-2000' },
                { label: '2000 - 2010', value: '2000-2010' },
                { label: '2010 - 2020', value: '2010-2020' },
            ]
        },

        getData (form) {
            this.charts = []

            if (!this.form.split
                || !this.form.sample
                || !this.selectedVar
            ) {
                return
            }

            // if (this.form.split === 'cross_country') {
            //     this.noData = true
            //
            //     return
            // }

            axios.post(route('charts.data'), form).then(response => {
                this.noData = false

                response.data.forEach((v, i) => {
                    let dataDirty = {},
                        chartOptions = { ...this.defaultChartOptions },
                        chartData,
                        selectedVarGroup,
                        selectedVar

                    // Get group of var
                    selectedVarGroup = this.findGroupWithVariable(this.selectedVar)

                    if (!selectedVarGroup) {
                        return
                    }

                    // Get var in group
                    selectedVar = this.findVariableInGroup(this.selectedVar, selectedVarGroup)
                    this.selectedVarObject = selectedVar

                    if (!selectedVar) {
                        return
                    }

                    // Set chart options
                    if (selectedVar.max) {
                        chartOptions.vAxis.maxValue = selectedVar.max

                        chartOptions.vAxis.viewWindow = {
                            min: 0,
                            max: selectedVar.max,
                        }

                        if (chartOptions.vAxis.maxValue !== 1) {
                            delete chartOptions.vAxis.ticks
                        }
                    }

                    if (this.oForm.split === 'income_class') {
                        chartOptions.hAxis = {
                            title: 'GDP p/c, log',
                            minValue: 0,
                            maxValue: 12,
                            viewWindow: {
                                min: 6,
                                max: 12,
                            },
                            ticks: [6, 7, 8, 9, 10, 11, 12],
                        }
                    }

                    if (selectedVar.special === true) {
                        // Remove all min max restrictions and let google decide which scale is best.
                        chartOptions.vAxis = {
                            title: '',
                        }
                    }

                    chartOptions._imageUrl = null
                    chartOptions.title = _.upperFirst(this.form.sample[i]) + ' - ' + selectedVar.label
                    chartOptions._description =
                        'Only the most recent observation available for each country ' +
                        'is used in this graph.<br>' +
                        '<strong class="font-bold">Fit lines</strong>: quadratic polynomials'

                    let filtered = v.filter(val => {
                        if (!val.hasOwnProperty(this.form.split) && this.form.split !== 'cross_country') {
                            return
                        }

                        if (!val.hasOwnProperty('lgdppc') || val.lgdppc === null) {
                            return
                        }

                        if (!val.hasOwnProperty(selectedVar.value) || val[selectedVar.value] === null) {
                            return
                        }

                        return val
                    })

                    filtered.forEach(val => {
                        let lgdppc = Math.trunc((+val.lgdppc.replace(',', '.')) * 100) / 100,
                            selectedVarValue = Math.trunc(+(val[this.selectedVar].replace(',', '.')) * 100) / 100,
                            n = this.form.split === 'cross_country' ? 1 : val[this.form.split]

                        if (!dataDirty.hasOwnProperty(val.country_id)) {
                            dataDirty[val.country_id] = []
                        }

                        /*
                            We always want the most recent observation for each country.
                            Since all data points are ordered by year, we can just overwrite.
                        */
                        dataDirty[val.country_id][0] = {
                            v: lgdppc,
                            f: val.country_label + ': ' + lgdppc,
                        }

                        dataDirty[val.country_id][n] = {
                            v: selectedVarValue,
                            f: selectedVarValue,
                        }
                    })

                    let options = []
                    chartOptions.trendlines = {}
                    chartOptions.series = {}

                    if (this.subOptions[this.form.split]) {
                        this.subOptions[this.form.split].forEach((val, i) => {
                            if (!val.value) {
                                return
                            }

                            options.push({
                                label: val.label,
                                type: 'number',
                            })

                            chartOptions.trendlines[i] = {
                                type: 'polynomial',
                                degree: 2,
                                visibleInLegend: true,
                                labelInLegend: val.label,
                                opacity: 1,
                                tooltip: false,
                            }

                            chartOptions.series[i] = {
                                dataOpacity: 0.2,
                                visibleInLegend: false,
                            }
                        })
                    }

                    if (this.form.split === 'cross_country') {
                        options.push(
                            {
                                label: '',
                                type: 'number',
                            },
                        )

                        chartOptions.trendlines[0] = {
                            type: 'polynomial',
                            degree: 2,
                            visibleInLegend: false,
                            labelInLegend: '',
                            opacity: 1,
                            tooltip: false,
                        }

                        chartOptions.series[0] = {
                            dataOpacity: 0.2,
                            visibleInLegend: false,
                        }
                    }

                    // Check if all arrays are complete
                    Object.entries(dataDirty).forEach(([key, val]) => {
                        options.forEach((o, idx) => {
                            if (!val[idx + 1]) {
                                dataDirty[key][idx + 1] = {
                                    f: '',
                                    v: null,
                                }
                            }
                        })
                    })

                    chartData = [
                        ['GDP p/c, log', ...options],
                    ].concat(Object.values(dataDirty))

                    let length = chartData[0].length
                    chartData = chartData.filter(element => {
                        if (element.length === length) {
                            return element
                        }
                    })

                    this.charts.push({
                        chartData: chartData,
                        chartOptions: chartOptions,
                        originalData: filtered,
                    })
                })
            }).catch((e) => {
                console.error(e)

                this.noData = true
            })
        },
    },
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style>
.multiselect-tag.bg-glm-red {
    background-color: rgba(210, 30, 35, 1) !important;
}

.multiselect-tag.bg-glm-red i::before {
    color: rgb(255, 208, 208) !important;
}

.multiselect-input {
    --tw-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);

    padding: 0.5rem 0.75rem;
    border-width: 1px;
    border-radius: 0.25rem;

    --tw-border-opacity: 1;
    border-color: rgba(224, 224, 224, var(--tw-border-opacity));

    margin-bottom: 0.25rem !important;
}
</style>
